import React, { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Menu, User, X } from "lucide-react";
import AccountCreation from "../AccountCreation";
import { signOut, useCurrentUser } from "../../lib/auth";
import { CONTACT_EMAIL, FREE_PROMO, LINKEDIN_URL, LOGO_SRC, REPORT_PRICE } from "../../config";
import { buttons, container } from "./ui";

const SECTIONS = [
  { id: "features", label: "Features" },
  { id: "pricing", label: "Pricing" },
  { id: "guide", label: "Guide" },
];

export function usePageTitle(title) {
  useEffect(() => {
    document.title = title ? `${title} | Car Sense` : "Car Sense | MOT history, mileage and common faults for any UK car";
  }, [title]);
}

// Scrolls to a section of the home page, going to the home page first if needed.
export function useSectionNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return useCallback(
    (id) => {
      const target = pathname === "/" && document.getElementById(id);
      if (target) {
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
      } else {
        navigate(`/#${id}`);
      }
    },
    [navigate, pathname]
  );
}

// "£2.99" crossed out, then "Free", while the free offer is running.
export function Price({ className = "", strikeClassName = "font-normal opacity-75" }) {
  if (!FREE_PROMO) return <span className={className}>{REPORT_PRICE}</span>;
  return (
    <span className={`inline-flex items-baseline gap-1.5 ${className}`}>
      <span className="sr-only">{`Free, was ${REPORT_PRICE}`}</span>
      <s aria-hidden="true" className={strikeClassName}>
        {REPORT_PRICE}
      </s>
      <span aria-hidden="true">Free</span>
    </span>
  );
}

export function Logo() {
  return (
    <Link
      to="/"
      className="inline-flex shrink-0 items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
    >
      <img src={LOGO_SRC} alt="Car Sense" className="h-10 w-auto" />
    </Link>
  );
}

function SectionLink({ id, className, onNavigate, children }) {
  const goToSection = useSectionNav();
  return (
    <a
      href={`/#${id}`}
      className={className}
      onClick={(event) => {
        event.preventDefault();
        // Close the mobile menu before measuring, or the scroll overshoots by its height.
        if (onNavigate) flushSync(onNavigate);
        goToSection(id);
      }}
    >
      {children}
    </a>
  );
}

function PromoBanner() {
  if (!FREE_PROMO) return null;
  return (
    <div className="bg-blue-700 text-white print:hidden">
      <div className={`${container} flex flex-wrap items-center justify-center gap-x-4 gap-y-1 py-2.5 text-center text-sm`}>
        <p>
          <span className="font-semibold">Limited Time Offer: Free Searches.</span>{" "}
          <span className="hidden text-blue-100 sm:inline">
            Full reports are <s>{REPORT_PRICE}</s> free for a limited time.
          </span>
        </p>
        <SectionLink
          id="search"
          className="inline-flex items-center gap-1 font-semibold underline decoration-blue-300 underline-offset-4 hover:decoration-white"
        >
          Check a car
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </SectionLink>
      </div>
    </div>
  );
}

export function SiteHeader() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const closeMenu = () => setMenuOpen(false);

  const openAuth = (mode) => {
    closeMenu();
    setAuthMode(mode);
  };

  const handleSignOut = () => {
    signOut();
    closeMenu();
    if (pathname === "/profile") navigate("/");
  };

  const accountActions = user ? (
    <>
      <Link to="/profile" className={buttons.ghost}>
        <User className="h-4 w-4" aria-hidden="true" />
        My searches
      </Link>
      <button type="button" onClick={handleSignOut} className={buttons.secondary}>
        Sign out
      </button>
    </>
  ) : (
    <>
      <button type="button" onClick={() => openAuth("login")} className={buttons.ghost}>
        Sign in
      </button>
      <button type="button" onClick={() => openAuth("signup")} className={buttons.primary}>
        Create account
      </button>
    </>
  );

  return (
    <>
      <PromoBanner />
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white print:hidden">
        <div className={`${container} flex h-16 items-center justify-between gap-4`}>
          <Logo />

          <nav aria-label="Main" className="hidden items-center gap-1 md:flex">
            {SECTIONS.map((section) => (
              <SectionLink
                key={section.id}
                id={section.id}
                className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                {section.label}
              </SectionLink>
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">{accountActions}</div>

          <button
            type="button"
            className={`${buttons.ghost} -mr-2 md:hidden`}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            <span className="sr-only">{menuOpen ? "Close menu" : "Open menu"}</span>
          </button>
        </div>

        {menuOpen && (
          <div id="mobile-menu" className="border-t border-slate-200 bg-white md:hidden">
            <nav aria-label="Main" className={`${container} flex flex-col gap-1 py-3`}>
              {SECTIONS.map((section) => (
                <SectionLink
                  key={section.id}
                  id={section.id}
                  onNavigate={closeMenu}
                  className="rounded-md px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-100"
                >
                  {section.label}
                </SectionLink>
              ))}
              <div className="mt-2 grid grid-cols-2 gap-2 border-t border-slate-200 pt-4">{accountActions}</div>
            </nav>
          </div>
        )}
      </header>

      {authMode && (
        <AccountCreation
          initialMode={authMode}
          onClose={() => setAuthMode(null)}
          onSuccess={() => setAuthMode(null)}
        />
      )}
    </>
  );
}

const footerLink = "text-slate-600 transition-colors hover:text-slate-900";

export function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50 print:hidden">
      <div className={`${container} grid gap-10 py-12 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr]`}>
        <div className="max-w-sm">
          <Link to="/" className="text-lg font-bold tracking-tight text-slate-900 hover:text-blue-700">
            Car Sense
          </Link>
          <p className="mt-4 text-sm leading-6 text-slate-600">
            Used car checks built on official DVSA MOT records, with the known faults for each model
            explained in plain English.
          </p>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-slate-900">Product</h2>
          <ul className="mt-4 space-y-3 text-sm">
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <SectionLink id={section.id} className={footerLink}>
                  {section.id === "guide" ? "Buyer's guide" : section.label}
                </SectionLink>
              </li>
            ))}
            <li>
              <Link to="/sample-report" className={footerLink}>
                Sample report
              </Link>
            </li>
            <li>
              <Link to="/manual-search" className={footerLink}>
                Search by make and model
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-slate-900">Help</h2>
          <ul className="mt-4 space-y-3 text-sm">
            <li>
              <Link to="/error" className={footerLink}>
                Report a problem
              </Link>
            </li>
            <li>
              <a href={`mailto:${CONTACT_EMAIL}`} className={footerLink}>
                Email us
              </a>
            </li>
            <li>
              <Link to="/cookie-preferences" className={footerLink}>
                Cookie preferences
              </Link>
            </li>
            <li>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className={footerLink}>
                LinkedIn
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-200">
        <div className={`${container} flex flex-col gap-3 py-6 text-xs leading-5 text-slate-500 md:flex-row md:justify-between md:gap-10`}>
          <p className="max-w-3xl">
            Car Sense gives general guidance based on MOT records and the known faults for each model. It
            is not a guarantee of a vehicle's condition and doesn't replace an inspection by a qualified
            mechanic. MOT data comes from the DVSA.
          </p>
          <p className="shrink-0">© {new Date().getFullYear()} Car Sense Solutions Ltd</p>
        </div>
      </div>
    </footer>
  );
}

export function PageShell({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-white text-slate-900">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-blue-700 focus:shadow"
      >
        Skip to main content
      </a>
      <SiteHeader />
      <main id="main" className="flex-1">
        {children}
      </main>
      <SiteFooter />
    </div>
  );
}
